import { useMemo, useState } from "react";

const kr = (n: number) => `${Math.round(n).toLocaleString("sv-SE")} kr`;

function Field({
  label,
  suffix,
  value,
  onChange,
  min,
  max,
  step,
}: {
  label: string;
  suffix?: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-xs text-muted-foreground">
          {value.toLocaleString("sv-SE")}
          {suffix ? ` ${suffix}` : ""}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-primary"
      />
    </label>
  );
}

/** Månadskostnad på husbilslån — annuitet, samma modell som banken räknar med. */
export function KbMonthlyCalc() {
  const [price, setPrice] = useState(650000);
  const [down, setDown] = useState(130000);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(10);

  const result = useMemo(() => {
    const loan = Math.max(0, price - down);
    const r = rate / 100 / 12;
    const n = years * 12;
    const monthly = r === 0 ? loan / n : (loan * r) / (1 - Math.pow(1 + r, -n));
    return { loan, monthly, total: monthly * n, interest: monthly * n - loan };
  }, [price, down, rate, years]);

  return (
    <div className="not-prose my-10 rounded-3xl border border-border bg-card p-5 md:p-7">
      <p className="eyebrow">Räkna själv · vårt eget verktyg</p>
      <h3 className="mt-1 font-display text-xl md:text-2xl">Vad kostar den per månad?</h3>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="space-y-5">
          <Field label="Pris" suffix="kr" value={price} onChange={setPrice} min={100000} max={2500000} step={10000} />
          <Field label="Kontantinsats" suffix="kr" value={down} onChange={setDown} min={0} max={price} step={10000} />
          <Field label="Ränta" suffix="%" value={rate} onChange={setRate} min={2} max={14} step={0.1} />
          <Field label="Löptid" suffix="år" value={years} onChange={setYears} min={1} max={15} step={1} />
        </div>
        <div className="rounded-2xl bg-secondary/40 p-5">
          <p className="text-sm text-muted-foreground">Månadskostnad (annuitet)</p>
          <p className="mt-1 font-display text-4xl">{kr(result.monthly)}</p>
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Lånebelopp</dt>
              <dd>{kr(result.loan)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Total räntekostnad</dt>
              <dd>{kr(result.interest)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Att betala totalt</dt>
              <dd>{kr(result.total)}</dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-muted-foreground">
            Husbilslån ligger normalt på 20 % kontantinsats och 10–15 års löptid. Försäkring,
            skatt, förvaring och service tillkommer.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Lastvikt kvar när familjen, vattnet och gasolen är ombord. */
export function KbPayloadCalc({
  presets,
}: {
  presets?: Array<{ label: string; total: number; service: number }>;
}) {
  const [total, setTotal] = useState(3500);
  const [service, setService] = useState(2950);
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(2);
  const [water, setWater] = useState(60);
  const [gasol, setGasol] = useState(2);
  const [gear, setGear] = useState(60);

  const payload = total - service;
  const used = adults * 75 + kids * 40 + water + gasol * 22 + gear;
  const left = payload - used;

  return (
    <div className="not-prose my-10 rounded-3xl border border-border bg-card p-5 md:p-7">
      <p className="eyebrow">Räkna själv · vårt eget verktyg</p>
      <h3 className="mt-1 font-display text-xl md:text-2xl">Hur mycket lastvikt blir kvar?</h3>

      {presets && presets.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {presets.slice(0, 4).map((p) => (
            <button
              key={p.label}
              onClick={() => {
                setTotal(p.total);
                setService(p.service);
              }}
              className="rounded-full border border-border bg-background px-3 py-1.5 text-xs hover:border-accent"
            >
              {p.label}
            </button>
          ))}
          <span className="self-center text-xs text-muted-foreground">← fordon ur vårt lager</span>
        </div>
      )}

      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="space-y-4">
          <Field label="Totalvikt" suffix="kg" value={total} onChange={setTotal} min={2500} max={7500} step={50} />
          <Field label="Tjänstevikt" suffix="kg" value={service} onChange={setService} min={2000} max={7000} step={10} />
          <Field label="Vuxna (75 kg/st)" value={adults} onChange={setAdults} min={0} max={6} step={1} />
          <Field label="Barn (40 kg/st)" value={kids} onChange={setKids} min={0} max={6} step={1} />
          <Field label="Färskvatten" suffix="liter" value={water} onChange={setWater} min={0} max={150} step={5} />
          <Field label="Gasolflaskor (11 kg)" value={gasol} onChange={setGasol} min={0} max={4} step={1} />
          <Field label="Övrig packning" suffix="kg" value={gear} onChange={setGear} min={0} max={400} step={10} />
        </div>
        <div
          className={`rounded-2xl p-5 ${
            left < 0 ? "bg-destructive/10" : left < 50 ? "bg-amber-500/10" : "bg-emerald-500/10"
          }`}
        >
          <p className="text-sm text-muted-foreground">Kvar av lastvikten</p>
          <p className="mt-1 font-display text-4xl">{Math.round(left)} kg</p>
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Lastvikt enligt reg.bevis</dt>
              <dd>{Math.round(payload)} kg</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Personer, vatten, gasol, packning</dt>
              <dd>{Math.round(used)} kg</dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-muted-foreground">
            {left < 0
              ? "Ekipaget är överlastat som det är inställt nu — kör med halvfull vattentank eller titta på en modell med högre totalvikt."
              : "Vi väger kundbilar i verkstaden i Kalix: skillnaden mellan broschyrens tjänstevikt och verklig vikt är ofta 60–120 kg."}
          </p>
        </div>
      </div>
    </div>
  );
}
